import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { performanceReviewService, userService } from '../../services/api';
import { FiArrowLeft } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import './ReviewForm.css';

export const ReviewForm = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isEditMode = reviewId && reviewId !== 'new';

  const [formData, setFormData] = useState({
    userId: '',
    reviewPeriodStart: '',
    reviewPeriodEnd: '',
    rating: 3,
    goals: '',
    achievements: '',
    areasOfImprovement: '',
    comments: '',
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingReview, setExistingReview] = useState(null);

  useEffect(() => {
    fetchEmployees();
    if (isEditMode) {
      fetchReview();
    }
  }, [isEditMode, reviewId]);

  const fetchEmployees = async () => {
    try {
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchReview = async () => {
    setLoading(true);
    try {
      const response = await performanceReviewService.getById(reviewId);
      setExistingReview(response.data);
      setFormData({
        userId: response.data.userId,
        reviewPeriodStart: response.data.reviewPeriodStart,
        reviewPeriodEnd: response.data.reviewPeriodEnd,
        rating: response.data.rating || 3,
        goals: response.data.goals || '',
        achievements: response.data.achievements || '',
        areasOfImprovement: response.data.areasOfImprovement || '',
        comments: response.data.comments || '',
      });
    } catch (err) {
      setError('Failed to load review');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.userId) {
      errors.push('Employee is required');
    }

    if (!formData.reviewPeriodStart) {
      errors.push('Period start date is required');
    }

    if (!formData.reviewPeriodEnd) {
      errors.push('Period end date is required');
    }

    if (formData.reviewPeriodStart && formData.reviewPeriodEnd) {
      const start = new Date(formData.reviewPeriodStart);
      const end = new Date(formData.reviewPeriodEnd);
      if (end < start) {
        errors.push('Period end date must be after start date');
      }
    }

    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      errors.push('Rating must be between 1 and 5');
    }

    if (!formData.goals.trim()) {
      errors.push('Goals are required');
    }

    if (!formData.achievements.trim()) {
      errors.push('Achievements are required');
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value,
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  const handleSaveDraft = async () => {
    setError(null);
    setLoading(true);

    try {
      if (isEditMode) {
        await performanceReviewService.update(reviewId, {
          ...formData,
          status: 'draft',
        });
      } else {
        await performanceReviewService.create({
          ...formData,
          status: 'draft',
        });
      }

      navigate('/performance-review');
    } catch (err) {
      setError('Failed to save draft');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (isEditMode) {
        await performanceReviewService.update(reviewId, {
          ...formData,
          status: 'submitted',
        });
      } else {
        await performanceReviewService.create({
          ...formData,
          status: 'submitted',
        });
      }

      navigate('/performance-review');
    } catch (err) {
      setError('Failed to submit review');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  if (!canEdit) {
    return (
      <div className="error-page">
        <p>You do not have permission to create or edit performance reviews.</p>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/performance-review')}
        >
          Back to Reviews
        </button>
      </div>
    );
  }

  if (loading && isEditMode) {
    return <div className="loading-page">Loading review...</div>;
  }

  return (
    <div className="review-form-container">
      <div className="form-header">
        <button
          className="btn-back"
          onClick={() => navigate('/performance-review')}
          disabled={loading}
        >
          <FiArrowLeft /> Back
        </button>
        <h1>{isEditMode ? 'Edit Review' : 'Create Review'}</h1>
      </div>

      {error && (
        <div className="alert alert-error">
          {error.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="review-form">
        <div className="form-section">
          <h2>Review Details</h2>

          <div className="form-group">
            <label htmlFor="userId">Employee *</label>
            <select
              id="userId"
              name="userId"
              value={formData.userId}
              onChange={handleInputChange}
              disabled={isEditMode || loading}
              required
            >
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="reviewPeriodStart">Period Start *</label>
              <input
                id="reviewPeriodStart"
                type="date"
                name="reviewPeriodStart"
                value={formData.reviewPeriodStart}
                onChange={handleInputChange}
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="reviewPeriodEnd">Period End *</label>
              <input
                id="reviewPeriodEnd"
                type="date"
                name="reviewPeriodEnd"
                value={formData.reviewPeriodEnd}
                onChange={handleInputChange}
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Overall Rating *</label>
            <div className="rating-selector">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  className={`rating-star ${
                    formData.rating >= rating ? 'active' : ''
                  }`}
                  onClick={() => handleRatingClick(rating)}
                  disabled={loading}
                >
                  ★
                </button>
              ))}
              <span className="rating-label">{formData.rating} / 5</span>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Review Content</h2>

          <div className="form-group">
            <label htmlFor="goals">Goals *</label>
            <textarea
              id="goals"
              name="goals"
              value={formData.goals}
              onChange={handleInputChange}
              placeholder="Describe the goals set for this period"
              rows={5}
              disabled={loading}
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="achievements">Achievements *</label>
            <textarea
              id="achievements"
              name="achievements"
              value={formData.achievements}
              onChange={handleInputChange}
              placeholder="Describe the achievements during this period"
              rows={5}
              disabled={loading}
              required
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="areasOfImprovement">Areas of Improvement</label>
            <textarea
              id="areasOfImprovement"
              name="areasOfImprovement"
              value={formData.areasOfImprovement}
              onChange={handleInputChange}
              placeholder="Areas where the employee can improve"
              rows={4}
              disabled={loading}
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="comments">Comments</label>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              placeholder="Additional comments or feedback"
              rows={4}
              disabled={loading}
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/performance-review')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
